<template>
	<div class="bg-body w-full">
		<div v-if="isAuthPageEnabled" class="max-w-boundary-desktop mx-auto flex min-h-svh w-full flex-col">
			<div class="p-view-padding flex w-full items-center justify-between">
				<Logo />
				<JMButton tag="a" :href="helpUrl" target="_blank" rel="noopener noreferrer">
					{{ $t(`auth.common.need_help`) }}
				</JMButton>
			</div>
			<div class="p-view-padding flex grow items-center justify-center gap-12 py-0!">
				<div v-if="isSignin" class="hidden flex-col gap-6 lg:flex">
					<img
						src="/images/login/cover.svg"
						:alt="$t('auth.hero.login_cover_alt')"
						width="560"
						height="570"
					/>
					<div class="flex flex-col gap-1">
						<div class="flex items-center gap-3.5 text-4xl font-semibold italic">
							<div>{{ $t("auth.hero.organize") }}</div>
							<div class="text-primary">/</div>
							<div>{{ $t("auth.hero.monitor") }}</div>
							<div class="text-primary">/</div>
							<div>{{ $t("auth.hero.follow") }}</div>
						</div>
						<div class="text-4xl font-semibold italic">
							{{ $t("auth.hero.your_patients_in_every_phase") }}
						</div>
					</div>
					<div class="max-w-120 text-lg">
						{{ $t("auth.hero.description") }}
					</div>
				</div>
				<div class="flex w-full max-w-140 min-w-80 flex-col gap-6">
					<Card size="huge" content-class="p-6! md:p-8!">
						<AuthForm v-model:type="type" v-model:step="step" :show-signup-link="isSignin" />
					</Card>

					<div>
						<n-collapse-transition :show="!isSignin && !isDataExport">
							<JMButton text type="primary" @click="setSignin()">
								<template #icon>
									<Icon name="carbon:arrow-left" />
								</template>
								{{ isLogged ? $t(`auth.common.back_to_dashboard`) : $t(`auth.common.back_to_login`) }}
							</JMButton>
						</n-collapse-transition>
					</div>
				</div>
			</div>
			<MainFooter :boxed="false" hide-version />
		</div>
	</div>
</template>

<script lang="ts" setup>
import type { FormStep, FormType } from "@/components/auth/types"
import { NCollapseTransition } from "naive-ui"
import { computed, onBeforeMount, ref } from "vue"
import { useRoute, useRouter } from "vue-router"
import Logo from "@/app-layouts/common/Logo.vue"
import MainFooter from "@/app-layouts/common/MainFooter.vue"
import AuthForm from "@/components/auth/AuthForm.vue"
import Card from "@/components/common/Card.vue"
import Icon from "@/components/common/Icon.vue"
import JMButton from "@/components/common/JMButton.vue"
import { useAuthStore } from "@/stores/auth"

const { formType } = defineProps<{
	formType?: FormType
}>()

const route = useRoute()
const router = useRouter()
const type = ref<FormType | undefined>(formType || undefined)
const step = ref<FormStep | undefined>(undefined)
const authStore = useAuthStore()
const isLogged = computed(() => authStore.isLogged)
const isSignin = computed(() => type.value === "signin" && (step.value === "credentials" || step.value === undefined))
const isDataExport = computed(() => type.value === "dataexport")
const isAuthPageEnabled = computed(() => !isLogged.value || isDataExport.value)
const helpUrl = computed(() => import.meta.env.VITE_LINK_FAQ)

function setSignin() {
	if (isDataExport.value) {
		router.push({ name: "Login" })
		return
	}
	type.value = "signin"
	step.value = "credentials"
}

onBeforeMount(() => {
	if (route.query.step) {
		const step = route.query.step as FormType
		type.value = step
	}
})
</script>
